/** Shared data shapes: weapons, loadouts and upgrades. Part of src/types.ts (re-exported there; import from '../types'). */

// =============================================================================
// WEAPONS
// =============================================================================

export type WeaponKey = 'standard' | 'heavy' | 'cluster' | 'nuke' | 'probe' | 'punch'
  | 'antigrav' | 'gravBomb' | 'tether' | 'terraformer' | 'emp' | 'drill' | 'fire' | 'mortar';

/** DIRECTION §12.7: a launcher's class, by reach and mount. Undefined = orbital (today's lob, whole). */
export type LauncherClass = 'handheld' | 'field' | 'orbital';

export interface WeaponDef {
  name: string;
  flavorName: string;     // ICC euphemism
  icon: string;
  blastRadius: number;
  damage: number;
  color: string;
  cbpCost: number;
  fuelDuration: number;   // seconds of engine burn (default)
  cluster?: {
    count: number;
    spread: number;
    subWeapon: WeaponKey;
  };
  reveals?: boolean;       // true for probe
  noCrater?: boolean;      // skip crater creation on impact
  knockbackForce?: number; // override knockback strength multiplier (default 1.0)
  special?: 'antigrav' | 'gravBomb' | 'tether' | 'terraformer' | 'emp' | 'drill' | 'fire';
  empDuration?: number;    // turns of EMP disable
  fireDuration?: number;   // turns fire zone persists
  fireDamagePerTurn?: number; // damage per turn in fire zone
  hitscan?: boolean;         // instant raycast, no projectile arc (railgun)
  /** §12.7 launcher class. `field` = intra-planet, high arc: the power cap keeps it below escape speed, the elevation floor keeps it steep. */
  rangeClass?: LauncherClass;
  /** Highest launch power this weapon can be fired at (the slider and the bot search both clamp to it). */
  maxPower?: number;
  /** Lowest elevation (rad) this weapon can be fired at — a mortar lobs, it does not graze. */
  minElevation?: number;
}

// =============================================================================
// LOADOUT — per-player weapon inventory and upgrades
// =============================================================================

/**
 * Player's ammo count for a specific weapon.
 * null = unlimited (default for standard weapon)
 */
export type AmmoCount = number | null;

/**
 * Heal items: small (30% HP) and large (100% HP).
 */
export type ItemKey = 'healSmall' | 'healLarge';

/**
 * Player's full loadout: ammo counts and items. (Gear no longer rides here —
 * it resolves into player.modifiers; see core/unitModifiers.ts.)
 */
export interface PlayerLoadout {
  ammo: Partial<Record<WeaponKey, AmmoCount>>;
  /** @deprecated Never read in play. Kept so existing loadout literals compile. */
  upgrades?: Record<string, never>;
  items?: Partial<Record<ItemKey, number>>;
}

/**
 * Default loadout: unlimited standard ammo, no upgrades.
 * Other weapons start with 0 ammo (must be purchased in economy/campaign).
 */
export function createDefaultLoadout(): PlayerLoadout {
  return {
    ammo: {
      standard: null,  // unlimited
      heavy: 3,
      cluster: 2,
      nuke: 1,
      probe: 2,
      punch: 3,
      antigrav: 0,
      gravBomb: 0,
      tether: 0,
      terraformer: 0,
      emp: 0,
      drill: 0,
      fire: 0,
      mortar: 0,
    },
    upgrades: {},
    items: {},
  };
}

/**
 * Sandbox loadout: unlimited everything for testing/casual play — except the launch weapon (owner, Oct 9:
 * antigrav is never infinite; a sandbox unit gets two).
 */
export function createSandboxLoadout(): PlayerLoadout {
  return {
    ammo: {
      standard: null,
      heavy: null,
      cluster: null,
      nuke: null,
      probe: null,
      punch: null,
      antigrav: 2,
      gravBomb: null,
      tether: null,
      terraformer: null,
      emp: null,
      drill: null,
      fire: null,
      mortar: 0,        // the field class is an experiment (S4): never in the sandbox by default
    },
    upgrades: {},
    items: { healSmall: 99, healLarge: 99 },
  };
}
